/**
 * DemocratCharts — useTrades.
 *
 * Loads the current user's trade list with optional filters. Returns
 * mutation helpers that update local state optimistically.
 */

import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

export interface TradeRow {
  id: string;
  symbol: string;
  exchange: string;
  timeframe: string;
  direction: "long" | "short";
  quantity: number;
  entry_ts: string;
  entry_price: number;
  sl_price: number | null;
  tp_price: number | null;
  exit_ts: string | null;
  exit_price: number | null;
  result: "open" | "win" | "loss" | "breakeven" | "manual";
  pnl: number | null;
  note: string | null;
  created_at: string;
  updated_at: string;
}

interface UseTradesParams {
  symbol?: string;
  exchange?: string;
  timeframe?: string;
}

export function useTrades(params: UseTradesParams = {}) {
  const [trades, setTrades] = useState<TradeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTrades = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await api.get<TradeRow[]>("/trades", { params });
      setTrades(resp.data);
    } catch (e) {
      setError((e as Error).message || "Failed to load trades");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.symbol, params.exchange, params.timeframe]);

  useEffect(() => {
    fetchTrades();
  }, [fetchTrades]);

  const createTrade = useCallback(async (payload: {
    symbol: string;
    exchange: string;
    timeframe: string;
    direction: "long" | "short";
    quantity: number;
    entry_ts: string;
    entry_price: number;
    sl_price?: number | null;
    tp_price?: number | null;
    account_balance_at_entry?: number;
    note?: string;
  }) => {
    const resp = await api.post<TradeRow>("/trades", payload);
    setTrades((cur) => [resp.data, ...cur]);
    return resp.data;
  }, []);

  const updateTrade = useCallback(async (id: string, updates: {
    sl_price?: number | null;
    tp_price?: number | null;
    note?: string;
  }) => {
    const resp = await api.patch<TradeRow>(`/trades/${id}`, updates);
    setTrades((cur) => cur.map((t) => (t.id === id ? resp.data : t)));
    return resp.data;
  }, []);

  const closeTrade = useCallback(async (id: string, payload: {
    exit_ts: string;
    exit_price: number;
    result?: "manual" | "win" | "loss" | "breakeven";
  }) => {
    const resp = await api.post<TradeRow>(`/trades/${id}/close`, {
      ...payload,
      result: payload.result ?? "manual",
    });
    setTrades((cur) => cur.map((t) => (t.id === id ? resp.data : t)));
    return resp.data;
  }, []);

  const deleteTrade = useCallback(async (id: string) => {
    await api.delete(`/trades/${id}`);
    setTrades((cur) => cur.filter((t) => t.id !== id));
  }, []);

  return {
    trades,
    loading,
    error,
    refetch: fetchTrades,
    createTrade,
    updateTrade,
    closeTrade,
    deleteTrade,
  };
}
